import type { ModulesResponse, NavEntry, NavGroup, Role } from "@riea-water/contracts";

/** The prototype's groups, in its order (docs/MODULES.md section 3). */
export const NAV_GROUPS: readonly { key: NavGroup; label: string }[] = [
  { key: "data_assurance", label: "Data Assurance" },
  { key: "modules", label: "Modules" },
  { key: "market", label: "Market" },
  { key: "insight", label: "Insight" },
  { key: "setup", label: "Setup" },
];

export interface NavItem extends NavEntry {
  badgeValue: number | null;
  /** Red badge: open critical findings. */
  hot: boolean;
}

export interface NavSection {
  key: NavGroup;
  label: string;
  items: NavItem[];
}

/**
 * Screens a role never sees (docs/UX.md section 4, the "none" cells). Every
 * other visibility difference is inside a screen, not in the navigation.
 */
const HIDDEN_FOR_ROLE: Partial<Record<Role, readonly string[]>> = {
  field_coordinator: ["/submissions", "/bilaterals", "/imports", "/rules", "/connections"],
};

export function visibleFor(route: string, role: Role): boolean {
  return !(HIDDEN_FOR_ROLE[role] ?? []).includes(route);
}

/**
 * Navigation from /modules alone (rule 1): the enabled modules' entries in
 * manifest order, grouped in the prototype's group order, badge values from
 * the response, filtered by what the role may see.
 */
export function buildNav(modules: ModulesResponse, role: Role): NavSection[] {
  const sections: NavSection[] = NAV_GROUPS.map((g) => ({ key: g.key, label: g.label, items: [] }));
  for (const module of modules.items) {
    for (const entry of module.nav) {
      if (!visibleFor(entry.route, role)) continue;
      const section = sections.find((s) => s.key === entry.group);
      if (!section) continue;
      const badgeValue = entry.badge ? (modules.badges[entry.badge] ?? 0) : null;
      section.items.push({
        ...entry,
        badgeValue,
        hot: entry.badge === "open_critical_findings" && (badgeValue ?? 0) > 0,
      });
    }
  }
  return sections.filter((s) => s.items.length > 0);
}

/** The route a signed-in user lands on: Overview when they have it, else the first entry. */
export function landingRoute(sections: NavSection[]): string {
  const all = sections.flatMap((s) => s.items);
  return all.find((i) => i.route === "/overview")?.route ?? all[0]?.route ?? "/overview";
}
